/** AI agents (ALP-83): Automate → Agents, playground, assignment, usage, inbox pickers. */
export const agents = {
  title: "Agents",
  subtitle: "AI personas that answer comments and DMs the way you want: their voice, what they know, where they answer and how much.",
  newAgent: "New agent",
  back: "All agents",
  empty: { title: "No agents yet", hint: "Create your first agent: give it a name, a tone and instructions, try it in the playground, then assign it to an account." },
  list: {
    assignments: "Assignments: {{n}}",
    notAssigned: "Not assigned yet",
    repliesMonth: "{{n}} replies this month",
    pending: "{{n}} drafts waiting",
    edit: "Open",
    version: "v{{n}}",
  },
  status: { active: "Active", paused: "Paused" },
  mode: {
    draft_for_approval: "Draft for approval",
    auto_send: "Send automatically",
    draftHint: "Every reply waits in Approvals until someone on the team approves it.",
    autoHint: "Replies go out right away, within the limits and working hours.",
    autoWarning: "Auto send posts replies publicly without review. Try the agent in the playground first and keep the limits tight.",
  },
  tabs: { configure: "Configure", playground: "Playground", assignments: "Where it answers", usage: "Usage & activity" },
  editor: {
    newTitle: "New agent",
    save: "Save",
    saving: "Saving…",
    create: "Create agent",
    unsaved: "Unsaved changes",
    saved: "Saved as version {{n}}",
    created: "Agent created",
    conflict: "Someone else changed this agent. Reload to see the latest version.",
    failed: "Couldn't save the agent",
    delete: "Delete agent",
    deleteConfirm: "Delete {{name}}? Its default assignments are removed; conversations it was assigned to go back to their defaults. History stays.",
    deleted: "Agent deleted",
    versions: "Versions",
    note: "What changed (optional)",
    notePlaceholder: "e.g. friendlier tone, new delivery info",
    required: "Give the agent a name",
    readOnly: "Only admins can change agents. You can still try it in the playground.",
    notFound: "This agent doesn't exist anymore.",
  },
  sections: {
    identity: "Identity",
    identityHint: "How the agent shows up for your team (customers never see the name).",
    persona: "Persona & tone",
    personaHint: "Who is talking? A short persona and a tone shape every reply.",
    instructions: "Instructions",
    instructionsHint: "What the agent does, what it knows, and how it behaves. Plain words work best.",
    rules: "Always / never",
    language: "Language",
    surfaces: "Reply style per place",
    surfacesHint: "Public replies are seen by everyone; private replies and DMs can say more.",
    model: "Model & creativity",
    knowledge: "Knowledge",
    knowledgeHint: "The agent searches your Knowledge before answering and only uses facts it finds.",
    handoff: "Hand over to a person",
    handoffHint: "When one of these applies, the agent doesn't answer: the item goes to your team.",
    hours: "Working hours",
    limits: "Limits",
    limitsHint: "Caps keep costs and surprises low. Replies above a limit are skipped with a reason in the activity log.",
    mode: "Delivery",
  },
  fields: {
    name: "Name",
    namePlaceholder: "e.g. Sales agent",
    emoji: "Emoji",
    pickEmoji: "Pick an emoji",
    description: "Description",
    descriptionPlaceholder: "What is this agent for?",
    status: "Status",
    persona: "Persona",
    personaPlaceholder: "e.g. Lina, the store's warm sales assistant who knows every product.",
    tone: "Tone",
    instructions: "Instructions (system prompt)",
    instructionsPlaceholder: "Answer questions about our products, sizes, prices and delivery…",
    insertSnippet: "Insert an example",
    dos: "Always",
    dosPlaceholder: "Type and press Enter, e.g. Invite to DM for sizes",
    donts: "Never",
    dontsPlaceholder: "e.g. Never promise discounts",
    remove: "Remove",
    examples: "Example replies",
    examplesHint: "The agent copies their style, never their facts.",
    exampleMessage: "Customer says",
    exampleReply: "Agent replies",
    addExample: "Add example",
    language: "Reply language",
    enabled: "Answers here",
    maxLength: "Max length",
    chars: "{{n}} characters",
    style: "Style notes",
    stylePlaceholder: "e.g. one sentence, invite to DM for prices",
    emojiLevel: "Emoji",
    model: "Model",
    temperature: "Creativity",
    temperatureHint: "Lower = consistent and literal; higher = varied wording.",
    datasets: "Search in",
    maxSnippets: "Snippets per reply",
    minScore: "Minimum match",
    uploadDocs: "Add brand / FAQ documents",
    keywords: "Keywords",
    keywordsPlaceholder: "e.g. refund, lawyer, استرجاع",
    intents: "Intents",
    categories: "Your AI categories",
    noCategories: "No AI categories yet. Create them in Settings → Tags.",
    negative: "Negative messages",
    whenUnsure: "When the agent isn't sure",
    assignTo: "Assign to",
    nobody: "Nobody",
    notify: "Notify admins",
    hoursEnabled: "Only answer during working hours",
    timezone: "Timezone",
    outside: "Outside working hours",
    outsideSkip: "Don't answer",
    outsideDraft: "Draft for approval",
    from: "From",
    to: "To",
    closed: "Closed",
    addWindow: "Add hours",
    perHour: "Per hour",
    perDay: "Per day",
    perConversation: "Per conversation per day",
    budgetShare: "Share of the monthly AI budget",
    noLimit: "No limit",
    percent: "{{n}}%",
  },
  tones: {
    friendly: "Friendly",
    professional: "Professional",
    playful: "Playful",
    luxury: "Luxury",
    empathetic: "Empathetic",
    concise: "Concise",
    custom: "Custom",
  },
  tonePreviews: {
    friendly: "“Hi Sara! So happy you love it 🌸 The 60×90 is 55 JOD.”",
    professional: "“Thank you for your interest. The 60×90 cm print is 55 JOD.”",
    playful: "“Ooh great taste! 😍 The 60×90 is yours for 55 JOD.”",
    luxury: "“A beautiful choice. The 60×90 cm edition is 55 JOD.”",
    empathetic: "“We're sorry for the wait, Sara. Your order ships tomorrow.”",
    concise: "“60×90 cm: 55 JOD. Free delivery in Amman.”",
    custom: "Describe the voice in the persona.",
  },
  snippets: {
    scope: "Only answer questions about our products, orders and delivery. For anything else, politely say a teammate will reply.",
    prices: "Use the prices in the knowledge. If a price isn't there, say we'll send it by DM.",
    dm: "For sizes, custom orders or personal details, invite the customer to send us a DM.",
    complaints: "If the customer is upset, apologise once, give the next step and hand over to a person.",
  },
  languages: {
    auto: "Same as the customer",
    ar: "Arabic",
    "ar-gulf": "Arabic (Gulf)",
    "ar-levant": "Arabic (Levant)",
    "ar-egypt": "Arabic (Egyptian)",
    "ar-maghreb": "Arabic (Maghrebi)",
    "ar-msa": "Arabic (Modern Standard)",
    en: "English",
  },
  surfaces: { comment_public: "Public comment reply", comment_private: "Private reply", dm: "Direct messages" },
  emoji: { none: "None", few: "A few", many: "Plenty" },
  models: {
    "malfak-smart": "Smart",
    "malfak-smartHint": "Best answers, a bit slower and costlier.",
    "malfak-fast": "Fast",
    "malfak-fastHint": "Quick and cheap, good for short replies.",
  },
  datasets: {
    brand: "Brand & FAQ documents",
    brandHint: "Documents you upload (prices, policies, FAQs).",
    content: "Past comments & DMs",
    contentHint: "How your team answered before.",
  },
  playground: {
    title: "Playground",
    hint: "Try the agent with the settings you are editing, saved or not.",
    nothingSent: "Nothing is sent: this is a preview.",
    samples: "Try a real message",
    noSamples: "No recent messages",
    surface: "Answer as",
    message: "Customer message",
    messagePlaceholder: "Type what a customer might say…",
    send: "Try it",
    trying: "Thinking…",
    reset: "Clear chat",
    you: "Customer",
    agent: "Agent",
    decision: { reply: "Would reply", handoff: "Hands over to a person", skip: "Would not answer" },
    knowledge: "Knowledge it found",
    noKnowledge: "No knowledge matched this message.",
    knowledgeOff: "Knowledge search isn't available here, so no snippets were used.",
    score: "{{n}}% match",
    violations: "Guardrails",
    violation: {
      too_long: "Too long: shortened",
      url_not_allowed: "Link not allowed",
      foreign_mention: "Mentions another account",
      phone_number: "Phone number",
      email: "Email address",
      language_mismatch: "Different language",
      empty: "Empty",
    },
    tokens: "{{n}} tokens",
    cost: "${{n}}",
    duration: "{{n}} ms",
    prompt: "What the model sees",
    failed: "The playground couldn't run",
  },
  reasons: {
    agent_paused: "The agent is paused",
    surface_disabled: "The agent doesn't answer here",
    outside_hours: "Outside working hours",
    limit_hour: "Hourly limit reached",
    limit_day: "Daily limit reached",
    limit_conversation: "Limit for this conversation reached",
    budget_share: "The agent used its share of the AI budget",
    plan_limit: "Your plan's agent replies are used up",
    ai_disabled: "AI is switched off by the platform",
    ai_budget: "The AI budget for this month is used up",
    ai_unavailable: "AI was unavailable",
    messaging_window_closed: "The 24h messaging window is closed",
    private_reply_window: "Private replies are only allowed within 7 days",
    already_replied: "Already replied",
    handoff_keyword: "Handoff keyword",
    handoff_intent: "Intent goes to a person",
    handoff_category: "Category goes to a person",
    handoff_negative: "Negative message goes to a person",
    handoff_model: "The agent wasn't sure",
    guardrails: "The draft broke the reply guardrails",
    conversation_off: "AI is off for this conversation",
    no_agent: "No agent assigned",
    not_applicable: "Not applicable",
  },
  runStatus: { drafted: "Drafted", sent: "Sent", skipped: "Skipped", handoff: "Handed over", failed: "Failed" },
  triggers: { auto: "Automatic", rule: "Rule", manual: "Draft with agent", playground: "Playground", batch: "Batch" },
  sources: {
    rule: "from rule {{label}}",
    conversation: "assigned to this conversation",
    post: "from the post default",
    account: "from account default",
    channel: "from the {{label}} default",
    any: "default",
  },
  assignments: {
    title: "Default for",
    hint: "Comments and DMs on these use this agent unless a rule or a conversation says otherwise.",
    order: "Order: rule → conversation → post → account → channel.",
    add: "Add a default",
    targetType: "Default for",
    types: { channel: "Every account of a channel", account: "An account", post: "A post" },
    target: "Which",
    surface: "For",
    surfaceOptions: { all: "Comments & DMs", comments: "Comments", dms: "DMs" },
    remove: "Remove",
    removed: "Default removed",
    added: "Now the default for {{label}}",
    empty: "Not a default anywhere yet.",
    conversations: "Assigned conversations",
    noConversations: "No conversations are assigned to this agent.",
    rules: "Rules using this agent",
    noRules: "No rule uses this agent yet.",
    pickAccount: "Pick an account",
    pickPost: "Pick a post",
    instagram: "Instagram",
  },
  usage: {
    today: "Replies today",
    month: "Replies this month",
    sent: "Sent this month",
    pending: "Drafts waiting",
    skipped: "Skipped this month",
    handoffs: "Handed over this month",
    cost: "AI cost this month",
    tokens: "{{n}} tokens",
    budget: "Budget share",
    noBudget: "No own budget cap",
    of: "of {{n}}",
    limitHour: "Hourly cap",
    limitDay: "Daily cap",
  },
  activity: {
    title: "Activity",
    empty: "No activity yet. Assign the agent or draft a reply to see it here.",
    filter: "Status",
    all: "All",
    openApproval: "Open approval",
    openItem: "Open",
    more: "Load more",
    version: "v{{n}}",
  },
  versions: {
    title: "Versions",
    hint: "Every save is a version. Runs and replies record the version that wrote them.",
    current: "Current",
    restore: "Restore",
    restored: "Restored version {{n}} (saved as version {{m}})",
    restoreConfirm: "Restore version {{n}}? It's saved as a new version; nothing is lost.",
  },
  picker: {
    label: "Assigned agent",
    off: "AI off",
    offHint: "No AI replies in this conversation: you reply manually.",
    inherit: "Use the default",
    handledBy: "This conversation is handled by: {{agent}} ({{source}})",
    nobody: "No agent handles this conversation.",
    updated: "Agent updated",
    turnedOff: "AI turned off for this conversation",
    failed: "Couldn't change the agent",
    manage: "Manage agents",
  },
  draft: {
    button: "Draft with agent",
    pick: "Draft with",
    drafting: "Drafting…",
    done: "{{agent}} drafted a reply: it's waiting in Approvals.",
    skipped: "{{agent}} didn't draft: {{reason}}",
    open: "Open approval",
    noAgent: "Pick an agent first",
    failed: "Couldn't draft a reply",
  },
  comment: {
    title: "AI agent",
    handledBy: "Handled by {{agent}} ({{source}})",
    none: "No agent answers this comment. Assign a default to the account or the post.",
  },
  approval: { draftedBy: "Drafted by {{agent}} v{{version}}" },
};
